import { Dropdown, Grid } from '@statsbomb/kitbag-components';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

export const DEFAULT_QUALITY_SETTINGS = {
  FRAMERATE: '30',
  MAX_BITRATE: '2500000',
  RESOLUTION: {
    height: '720',
    width: '1280',
  },
};

const VALID_QUALITY_SETTINGS = {
  BITRATE: ['1500000', '2000000', '2500000', '3000000', '4000000', '5000000'],
  FRAMERATE: ['30', '60'],
  RESOLUTION: [
    {
      height: '720',
      width: '1280',
    },
    {
      height: '1080',
      width: '1920',
    },
  ],
};

function Bitrate({ setBitrate, bitrate }) {
  const intl = useIntl();

  const options = VALID_QUALITY_SETTINGS.BITRATE.map((rate) => ({
    label: rate,
    value: rate,
  }));

  return (
    <Grid item xs={4}>
      <Dropdown
        id="quality-settings-max_bitrate"
        label={intl.formatMessage({
          defaultMessage: 'Max Average Bitrate',
          id: '650ee',
        })}
        options={options}
        onChange={(selected) => setBitrate(selected.value)}
        value={options.find((option) => option.value === bitrate)}
        variant="secondary"
      />
    </Grid>
  );
}

Bitrate.propTypes = {
  bitrate: PropTypes.string.isRequired,
  setBitrate: PropTypes.func.isRequired,
};

function Framerate({ framerate, setFramerate }) {
  const intl = useIntl();

  const options = VALID_QUALITY_SETTINGS.FRAMERATE.map((rate) => ({
    label: rate.toString(),
    value: rate.toString(),
  }));

  return (
    <Grid item xs={4}>
      <Dropdown
        id="quality-settings-framerate"
        label={intl.formatMessage({
          defaultMessage: 'Framerate',
          id: 'bcf96',
        })}
        options={options}
        onChange={(selected) => setFramerate(selected.value)}
        value={options.find((option) => option.value === framerate)}
        variant="secondary"
      />
    </Grid>
  );
}

Framerate.propTypes = {
  framerate: PropTypes.string.isRequired,
  setFramerate: PropTypes.func.isRequired,
};

function Resolution({ resolution, setResolution }) {
  const intl = useIntl();
  const options = VALID_QUALITY_SETTINGS.RESOLUTION.map((resolutionOption) => ({
    label: `${resolutionOption.height} x ${resolutionOption.width}`,
    value: resolutionOption.height,
  }));

  return (
    <Grid item xs={4}>
      <Dropdown
        id="quality-settings-resolution"
        label={intl.formatMessage({
          defaultMessage: 'Resolution',
          id: 'b5a4b',
        })}
        options={options}
        onChange={(selected) =>
          setResolution(
            VALID_QUALITY_SETTINGS.RESOLUTION.find(
              (resolutionOption) => resolutionOption.height === selected.value,
            ),
          )
        }
        value={options.find((option) => option.value === resolution.height)}
        variant="secondary"
      />
    </Grid>
  );
}

Resolution.propTypes = {
  resolution: PropTypes.shape({
    displayValue: PropTypes.string,
    height: PropTypes.string,
    width: PropTypes.string,
  }).isRequired,
  setResolution: PropTypes.func.isRequired,
};

export function VideoQualitySettings({ qualitySettings, setQualitySettings }) {
  const handleResolutionChange = (resolution) =>
    setQualitySettings((currentValue) => ({ ...currentValue, resolution }));

  const handleFramerateChange = (framerate) =>
    setQualitySettings((currentValue) => ({ ...currentValue, framerate }));

  const handleBitrateChange = (bitrate) =>
    setQualitySettings((currentValue) => ({
      ...currentValue,
      bitrate,
    }));

  return (
    <Grid>
      <Resolution
        resolution={qualitySettings.resolution}
        setResolution={handleResolutionChange}
      />
      <Framerate
        framerate={qualitySettings.framerate}
        setFramerate={handleFramerateChange}
      />
      <Bitrate
        bitrate={qualitySettings.bitrate}
        setBitrate={handleBitrateChange}
      />
    </Grid>
  );
}
VideoQualitySettings.propTypes = {
  qualitySettings: PropTypes.shape({
    bitrate: PropTypes.string,
    framerate: PropTypes.string,
    resolution: PropTypes.shape({
      height: PropTypes.string,
      width: PropTypes.string,
    }),
  }).isRequired,
  setQualitySettings: PropTypes.func.isRequired,
};

export default { VideoQualitySettings };
